import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  IconButton
} from '@mui/material';
import { Login, PersonAdd, Close } from '@mui/icons-material';
import Cookies from 'js-cookie';

const CommenterSignInForm = ({ onSuccess, onCancel, onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.password.trim()) {
      setError('Password is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const response = await fetch(`${API_BASE_URL}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Sign in failed');
      }

      // Check if user role is allowed for blog commenting
      if (data.user.role !== 'affiliate' && data.user.role !== 'commenter') {
        throw new Error('Access denied. Invalid user role.');
      }

      // Store the token
      Cookies.set('auth_token', data.token, { expires: 7 });
      
      // Call success callback with user data
      onSuccess(data.user);
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: '400px',
        width: '100%'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: '#f8f9fa',
          p: 2,
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#212529' }}>
          Sign In to Comment
        </Typography>
        <IconButton 
          onClick={onCancel} 
          size="small"
          sx={{ 
            color: '#6c757d',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Form */}
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Email Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#495057', mb: 1, fontWeight: 500 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              name="email"
              type="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#007bff',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '16px'
                }
              }}
            />
          </Box>

          {/* Password Field */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#495057', mb: 1, fontWeight: 500 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              name="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#007bff',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '16px'
                }
              }}
            />
          </Box>

          {/* Sign In Button */}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{
              backgroundColor: '#007bff',
              borderRadius: '8px',
              py: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(0,123,255,0.2)',
              '&:hover': {
                backgroundColor: '#0056b3',
                boxShadow: '0 4px 8px rgba(0,123,255,0.3)'
              },
              '&:disabled': {
                backgroundColor: '#6c757d',
                boxShadow: 'none'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>
        </Box>

        {/* Switch to Register */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" sx={{ color: '#6c757d', mb: 1 }}>
            Don't have an account?
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={onSwitchToRegister}
            disabled={loading}
            sx={{
              color: '#007bff',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              },
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mx: 'auto'
            }}
          >
            <PersonAdd sx={{ fontSize: 16 }} />
            Register as Commenter
          </Link>
        </Box>

        {/* Footer Text */}
        <Typography variant="caption" sx={{ 
          color: '#6c757d', 
          textAlign: 'center', 
          display: 'block',
          mt: 2,
          lineHeight: 1.4
        }}>
          Sign in to participate in discussions and leave comments.
        </Typography>
      </Box>
    </Box>
  );
};

export default CommenterSignInForm;
